import {useQuery} from '@tanstack/react-query';
import {Link, useNavigate} from 'react-router-dom';
import {api} from '../api/client';
import Icon from '../components/Icon';
import {useDraftIndex} from '../components/tell/DraftBanner';
import {useAuth, useMe} from '../hooks/useAuth';
import {withReturn} from '../hooks/useReturnTo';
import {ThemeMode, useTheme} from '../hooks/useTheme';
import {AppInfo} from '../types';
import Button from '../components/Button';

const MODES: {value: ThemeMode; label: string}[] = [
    {value: 'light', label: 'Light'},
    {value: 'dark', label: 'Dark'},
    {value: 'system', label: 'Match phone'}
];

export default function MorePage() {
    const {claims, logout} = useAuth();
    const me = useMe();
    const {mode, setMode} = useTheme();
    const navigate = useNavigate();
    const info = useQuery({queryKey: ['app-info'], queryFn: () => api<AppInfo>('/api/app-info'), staleTime: Infinity});
    const tellDraft = useDraftIndex().find(d => d.key === 'newTell');

    const signOut = async () => {
        await logout();
        navigate('/', {replace: true});
    };

    return (
        <div className="settle-in flex flex-col gap-6">
            <header className="pt-2">
                <h1 className="display m-0">More</h1>
            </header>

            <section className="flex flex-col gap-2">
                <div className="band-label text-ink-2">You</div>
                <div className="rows rounded-[18px] border-[1.5px] border-line bg-surface">
                    <Link to="/welcome" className="flex min-h-[64px] items-center gap-3 px-4 no-underline">
                        <span className="flex h-10 w-10 items-center justify-center rounded-pill bg-shoot-soft font-display text-lg font-bold text-shoot-text">
                            {(me.data?.firstName ?? claims?.email ?? '?').slice(0, 1).toUpperCase()}
                        </span>
                        <span className="min-w-0 flex-1">
                            <span className="block truncate font-semibold">{me.data?.firstName ?? 'Set your name'}</span>
                            <span className="meta block truncate">
                                {claims?.email}
                                {me.data?.hemisphere ? ` · ${me.data.hemisphere === 'S' ? 'Southern' : 'Northern'} hemisphere` : ''}
                            </span>
                        </span>
                        <Icon name="chevron" size={20} className="text-ink-3" />
                    </Link>
                </div>
            </section>

            <section className="flex flex-col gap-2">
                <div className="band-label text-ink-2">Your garden</div>
                <div className="rows rounded-[18px] border-[1.5px] border-line bg-surface">
                    <Link to={withReturn('/places', tellDraft?.route)} className="flex min-h-[56px] items-center gap-3 px-4 no-underline">
                        <Icon name="pin" size={20} className="text-shoot-text" />
                        <span className="flex-1">
                            <span className="block font-semibold">Places</span>
                            <span className="meta block">Areas, beds, pots and spots a tell can point at</span>
                        </span>
                        <Icon name="chevron" size={20} className="text-ink-3" />
                    </Link>
                </div>
            </section>

            <section className="flex flex-col gap-2">
                <div className="band-label text-ink-2">Your data</div>
                <div className="rows rounded-[18px] border-[1.5px] border-line bg-surface">
                    <Link to="/export" className="flex min-h-[56px] items-center gap-3 px-4 no-underline">
                        <Icon name="share" size={20} className="text-ink-2" />
                        <span className="flex-1">
                            <span className="block font-semibold">Export everything</span>
                            <span className="meta block">Tells as JSON and CSV, photos as zips</span>
                        </span>
                        <Icon name="chevron" size={20} className="text-ink-3" />
                    </Link>
                    <Link to="/deleted" className="flex min-h-[56px] items-center gap-3 px-4 no-underline">
                        <Icon name="close" size={20} className="text-ink-2" />
                        <span className="flex-1">
                            <span className="block font-semibold">Recently deleted</span>
                            <span className="meta block">Recoverable for 30 days</span>
                        </span>
                        <Icon name="chevron" size={20} className="text-ink-3" />
                    </Link>
                </div>
            </section>

            <section className="flex flex-col gap-2">
                <div className="band-label text-ink-2">Assistants</div>
                <div className="rows rounded-[18px] border-[1.5px] border-line bg-surface">
                    <Link to="/assistants" className="flex min-h-[56px] items-center gap-3 px-4 no-underline">
                        <Icon name="leaf" size={20} className="text-shoot-text" />
                        <span className="flex-1">
                            <span className="block font-semibold">Connect an assistant</span>
                            <span className="meta block">Claude, ChatGPT, Claude Code: read and write your diary as you</span>
                        </span>
                        <Icon name="chevron" size={20} className="text-ink-3" />
                    </Link>
                </div>
            </section>

            <section className="flex flex-col gap-2">
                <div className="band-label text-ink-2">Appearance</div>
                <div className="flex gap-2">
                    {MODES.map(m => (
                        <button key={m.value} type="button" className="chip" aria-pressed={mode === m.value} onClick={() => setMode(m.value)}>
                            {m.label}
                        </button>
                    ))}
                </div>
            </section>

            {claims?.isAdmin && (
                <section className="flex flex-col gap-2">
                    <div className="band-label text-ink-2">Admin</div>
                    <div className="rows rounded-[18px] border-[1.5px] border-line bg-surface">
                        <Link to="/admin/users" className="flex min-h-[56px] items-center gap-3 px-4 no-underline">
                            <Icon name="hash" size={20} className="text-shoot-text" />
                            <span className="flex-1">
                                <span className="block font-semibold">People</span>
                                <span className="meta block">Who has asked to join, who is in, who has used it</span>
                            </span>
                            <Icon name="chevron" size={20} className="text-ink-3" />
                        </Link>
                    </div>
                </section>
            )}

            <section className="flex flex-col gap-2">
                <div className="band-label text-ink-2">About</div>
                <div className="rounded-[18px] bg-marigold-soft p-4 text-marigold-text">
                    <div className="font-display text-lg font-[650] leading-tight">
                        {info.data ? `Phase ${info.data.phase} · ${info.data.phaseName}` : 'Sow and Tell'}
                    </div>
                    <p className="m-0 mt-1 text-sm">{info.data?.summary ?? 'An invite-only trial, built one small release at a time.'}</p>
                    {info.data?.next && <p className="m-0 mt-2 text-sm font-semibold">Next: {info.data.next}</p>}
                </div>
                <Link to="/" className="meta px-1 no-underline">
                    What Sow and Tell is, and the plan
                </Link>
            </section>

            <Button variant="secondary" danger busyLabel="Signing out…" onClick={signOut}>
                Sign out
            </Button>
        </div>
    );
}
